import { PaginationModel } from '@_types/molecules';
import getComponentMarkup from '@helpers/getComponentMarkup';
import CarouselMobilePagination from '@organisms/carousel/CarouselMobilePagination';
import { ComponentProps } from 'react';
import { getClassFunction } from '@helpers/functions';
import Swiper from 'swiper';
import debounce from 'debounce';
import { Autoplay, Manipulation, Navigation, Pagination as SwiperPagination } from 'swiper/modules';
import { PaginationOptions, SwiperOptions } from 'swiper/types';
import Pagination from '@molecules/pagination/Pagination';
import { CarouselModel } from '@_types/organisms';
import { currentViewport } from '@helpers/breakpoint';

// Swiper.use is not a React Hook, so we can safely disable the lint warning for hooks here
// eslint-disable-next-line
Swiper.use([Navigation, SwiperPagination, Autoplay, Manipulation]);

const getClass = getClassFunction('kf-o-carousel');
const stopAutoPlayEvent = 'stop-auto-play-event';
const startAutoPlayEvent = 'start-auto-play-event';
const scrollToTopBlockList = ['trackRecord'];

class Carousel {
  carousel: HTMLElement;
  swiper: Swiper | undefined;
  hasPaginationClickable: boolean | undefined;
  hasLooping: boolean = false;
  pagination: HTMLElement | null;
  paginationTemporaty: HTMLElement | null;
  itemType: CarouselModel['itemType'];
  spaceBetween: number = 0;
  disableAutoHeight = false;
  navigationContainer: HTMLElement | null;
  maxPages: number = 0;
  maxIndex: number = 0;
  totalSlides: number = 0;
  edgeSlidesClickabe: boolean = false;
  isTheView: boolean = false;

  constructor(carousel: HTMLElement) {
    this.carousel = carousel;
    this.hasPaginationClickable = this.carousel.dataset.hasPaginationClickable === 'true';
    this.spaceBetween = isNaN(parseInt(this.carousel.dataset.spaceBetween ?? '')) ? 0 : parseInt(this.carousel.dataset.spaceBetween ?? '');
    this.hasLooping = this.carousel.dataset.hasLooping === 'true';
    this.pagination = this.carousel.querySelector<HTMLElement>(getClass('pagination'));
    this.paginationTemporaty = document.createElement('div');
    this.paginationTemporaty.classList.add('hide');
    this.itemType = this.carousel.dataset.itemType as CarouselModel['itemType'];
    this.disableAutoHeight = this.carousel.dataset.disableAutoHeight === 'true';
    this.navigationContainer = this.carousel.querySelector<HTMLElement>('.kf-o-carousel__navigation-container');
    this.edgeSlidesClickabe = this.carousel.dataset.edgeSlidesClickable === 'true';
    this.isTheView = document.body.classList.contains('kf-the-view');
  }

  getBreakpointsConfig(itemType: string): SwiperOptions['breakpoints'] {
    switch (itemType) {
      case 'contentTile':
        return {
          0: {
            spaceBetween: 16,
            slidesPerView: 'auto',
            centeredSlides: false,
          },
          768: {
            slidesPerGroup: 2,
            centeredSlides: false,
            spaceBetween: 16,
          },
          1280: {
            slidesPerGroup: 3,
            centeredSlides: false,
            spaceBetween: 20,
          },
          1920: {
            slidesPerGroup: 3,
            centeredSlides: false,
            spaceBetween: 24,
          },
        };
      case 'propertyListing':
        return {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
            centeredSlides: false,
            spaceBetween: 24,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
          768: {
            slidesPerView: 2,
            slidesPerGroup: 2,
            centeredSlides: false,
            spaceBetween: 16,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
          1280: {
            slidesPerView: 4,
            slidesPerGroup: 4,
            centeredSlides: false,
            spaceBetween: 20,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
          1920: {
            slidesPerView: 4,
            slidesPerGroup: 4,
            centeredSlides: false,
            spaceBetween: 24,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
        };
      case 'propertyFeature':
        return {
          0: {
            spaceBetween: 24,
            slidesPerView: 1,
            slidesPerGroup: 1,
            centeredSlides: false,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
        };
      case 'insight':
        return {
          0: {
            spaceBetween: this.isTheView ? 13 : 16,
            slidesPerView: 1,
            centeredSlides: false,
          },
          768: {
            spaceBetween: 16,
            slidesPerView: 2,
            slidesPerGroup: 2,
            centeredSlides: false,
          },
          1280: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            centeredSlides: false,
            spaceBetween: 20,
          },
          1920: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            centeredSlides: false,
            spaceBetween: 24,
          },
        };
      case 'insightGroup':
        return {
          0: {
            spaceBetween: 16,
            slidesPerView: 1,
            centeredSlides: false,
            slidesPerGroup: 1,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
          768: {
            spaceBetween: 16,
            slidesPerView: 2,
            centeredSlides: false,
            slidesPerGroup: 2,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
          1280: {
            slidesPerView: 3,
            centeredSlides: false,
            spaceBetween: 20,
            slidesPerGroup: 3,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
          1920: {
            slidesPerView: 3,
            centeredSlides: false,
            spaceBetween: 24,
            slidesPerGroup: 3,
            pagination: {
              // @ts-expect-error custom property
              maxPages: 6,
            },
          },
        };
      case 'podcast':
        return {
          0: {
            spaceBetween: 13,
            slidesPerView: 1,
            centeredSlides: false,
          },
          768: {
            spaceBetween: 16,
            slidesPerView: 2,
            slidesPerGroup: 2,
            centeredSlides: false,
          },
          1280: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            centeredSlides: false,
            spaceBetween: 20,
          },
          1920: {
            slidesPerView: 3,
            slidesPerGroup: 3,
            centeredSlides: false,
            spaceBetween: 24,
          },
        };
      case 'propertyCategory':
        return {
          0: {
            spaceBetween: 24,
            slidesPerView: 'auto',
            slidesPerGroup: 2,
            centeredSlides: false,
          },
          768: {
            spaceBetween: 16,
            slidesPerView: 'auto',
            slidesPerGroup: 4,
            centeredSlides: false,
          },
          1280: {
            spaceBetween: 20,
            slidesPerView: 'auto',
            slidesPerGroup: 6,
            centeredSlides: false,
          },
          1920: {
            spaceBetween: 24,
            slidesPerView: 'auto',
            slidesPerGroup: 6,
            centeredSlides: false,
          },
        };
      case 'quote':
        return {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
            spaceBetween: 20,
            centeredSlides: false,
          },
          768: {
            slidesPerView: 1,
            slidesPerGroup: 1,
            spaceBetween: 24,
            centeredSlides: false,
          },
        };

      case 'categoryImage':
        return {
          0: {
            spaceBetween: 16,
            slidesPerView: 'auto',
            slidesPerGroup: 2,
            centeredSlides: false,
          },
          768: {
            spaceBetween: 16,
            slidesPerView: 'auto',
            slidesPerGroup: 4,
            centeredSlides: false,
          },
          1280: {
            spaceBetween: 16,
            slidesPerView: 'auto',
            slidesPerGroup: 6,
            centeredSlides: false,
          },
          1920: {
            spaceBetween: 16,
            slidesPerView: 'auto',
            slidesPerGroup: 6,
            centeredSlides: false,
          },
        };

      default:
        return {
          768: {
            spaceBetween: 24,
            slidesPerView: 1,
            centeredSlides: false,
          },
        };
    }
  }

  get swiperParams(): SwiperOptions | undefined {
    if (!this.carousel) {
      return undefined;
    }

    const hasNavigation = this.carousel.dataset.hasNavigation === 'true';

    this.totalSlides = this.carousel.querySelectorAll<HTMLElement>('.swiper-slide')?.length;
    const isLoopMode = this.hasLooping && this.totalSlides > 1;

    const isVideoCarousel = this.carousel.closest('.kf-o-video-carousel');
    const waitForImages = (container: HTMLElement): Promise<void[]> => {
      const images = Array.from(container.querySelectorAll<HTMLImageElement>('img'));

      return Promise.all(
        images.map((img) => {
          if (img.complete) return Promise.resolve();

          return Promise.race([
            new Promise<void>((resolve) => {
              img.addEventListener('load', () => resolve(), { once: true });
              img.addEventListener('error', () => resolve(), { once: true });
            }),
            new Promise<void>((resolve) => setTimeout(resolve, 5000)),
          ]);
        })
      );
    };

    let currentHeight = 0;
    const handleResize = (swiper: Swiper) => {
      const slideWrapper = swiper.el.querySelector<HTMLElement>('.swiper-wrapper');

      const maxHeightOfSlides = Math.max(
        ...Array.from(swiper.slides).map((slide) => {
          const child =
            slide.querySelector<HTMLElement>('.kf-o-content-tile__inner') ||
            slide.querySelector<HTMLElement>('.kf-o-podcasts-item__inner') ||
            slide.querySelector<HTMLElement>('.kf-o-property-listing-item');

          return child ? child.clientHeight : 0;
        })
      );

      if (currentHeight === maxHeightOfSlides) {
        return;
      }
      currentHeight = maxHeightOfSlides;

      if (slideWrapper) {
        slideWrapper.style.height = `${maxHeightOfSlides}px`;
      }
      swiper.slides.forEach((slide) => {
        slide.style.height = `${maxHeightOfSlides}px`;
      });
    };

    return {
      speed: 400,
      spaceBetween: this.spaceBetween,
      init: false,
      [this.totalSlides >= 4 ? 'loop' : 'rewind']: isLoopMode,
      loopAdditionalSlides: isLoopMode && this.totalSlides >= 4 ? 1 : 0,
      autoHeight: isVideoCarousel ? false : !this.disableAutoHeight,
      slidesPerView: 'auto',
      centeredSlides: true,
      navigation: !hasNavigation
        ? false
        : {
            nextEl: this.carousel.querySelector<HTMLElement>(getClass('navigation-button-next')),
            prevEl: this.carousel.querySelector<HTMLElement>(getClass('navigation-button-prev')),
          },
      updateOnWindowResize: true,
      on: {
        afterInit: ['contentTile', 'propertyListing', 'quote', 'podcast'].includes(this.itemType || '')
          ? (swiper) => {
              waitForImages(swiper.el).then(() => handleResize(swiper));
            }
          : undefined,
        resize: ['contentTile', 'propertyListing', 'quote', 'podcast'].includes(this.itemType || '')
          ? (swiper) => {
              this.carousel.classList.add('kf-o-carousel--resizing');
              debounce(() => {
                handleResize(swiper);
                this.carousel.classList.remove('kf-o-carousel--resizing');
              }, 300)();
            }
          : undefined,
        breakpoint: (swiper, breakpointParams) => this.handleOnBreakpoint(swiper, breakpointParams),
      },
      pagination: {
        el: this.paginationTemporaty,
        bulletClass: 'kf-m-pagination__page',
        clickable: this.hasPaginationClickable,
        type: 'custom',
        renderCustom: (_: Swiper, current: number, total: number) => {
          if (typeof current === 'number' && typeof total === 'number') {
            this.renderPagination(current, total);
            this.renderMobilePagination(current, total);
          }
        },
      } as PaginationOptions,
      breakpoints: this.getBreakpointsConfig(this.itemType || ''),
    };
  }

  addKeyboardEventIntoPagination() {
    if (!this.hasPaginationClickable) return;

    const pageContainer = this.carousel.querySelector<HTMLDivElement>('.kf-m-pagination');

    pageContainer?.addEventListener('keydown', (event) => this.handlePaginationKeyboardEvent(event));
  }

  addClickEventIntoPagination() {
    if (!this.hasPaginationClickable) return;

    const pageContainer = this.carousel.querySelector<HTMLDivElement>('.kf-m-pagination');

    pageContainer?.addEventListener('click', (event) => this.handlePaginationClick(event));
  }

  addKeyboardEventIntoNavArrows() {
    const prevArrow = this.carousel.querySelector<HTMLElement>(getClass('navigation-button-prev'));
    const nextArrow = this.carousel.querySelector<HTMLElement>(getClass('navigation-button-next'));

    prevArrow?.addEventListener('keydown', (event) => this.handleNavArrowsKeyboardEvents(event, 'prev'));
    nextArrow?.addEventListener('keydown', (event) => this.handleNavArrowsKeyboardEvents(event, 'next'));
  }

  handleNavArrowsKeyboardEvents(event: KeyboardEvent, type: 'next' | 'prev') {
    if (event.key === 'Enter') {
      if (type === 'next') {
        this.swiper?.slideNext();
      } else {
        this.swiper?.slidePrev();
      }
      this.swiper?.update();
    }
  }

  handleScrollToTopOfBlock() {
    if (!scrollToTopBlockList.includes(this.itemType || '')) return;

    if (this.itemType === 'trackRecord' && currentViewport() !== 'desktop') {
      window.scrollTo({ top: this.carousel.offsetTop, behavior: 'smooth' });
    }
  }

  handleSlideChange(swiper: Swiper) {
    this.handleScrollToTopOfBlock();
    this.handleSwiperPreventTabbing(swiper);
  }

  handlePaginationKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      const target = event.target as HTMLAnchorElement;

      if (!target) return;

      const page = target.dataset.pager;

      if (page) {
        if (this.hasLooping) {
          this.swiper?.slideToLoop(+page - 1);
        } else {
          this.swiper?.slideTo(+page - 1);
        }
      }

      this.carousel.querySelector<HTMLAnchorElement>(`.kf-m-pagination__page[data-pager="${page}"]`)?.focus();
    }
  }

  handlePaginationClick(event: MouseEvent) {
    const target = event.target as HTMLAnchorElement;

    if (!target) return;

    const page = target.dataset.pager;

    if (page) {
      if (this.hasLooping) {
        this.swiper?.slideToLoop(+page - 1);
      } else {
        this.swiper?.slideTo(+page - 1);
      }
    }
  }

  toggleAutoplay(isPaused?: boolean) {
    if (!this.swiper) return;

    if (isPaused) {
      this.swiper.autoplay.stop();
    } else {
      this.swiper.autoplay.start();
    }
  }

  handleSwiperPreventTabbing(swiper: Swiper) {
    swiper.slides.forEach((slide) => {
      slide.setAttribute('inert', 'true');
    });
    const slidesPerView = (swiper.slidesPerViewDynamic() as number) || 1;
    const slideWidth = swiper.slides[0]?.clientWidth || 0;
    const swiperPadding = parseInt(getComputedStyle(swiper.el).paddingLeft || '0', 10);

    // caculate the number of slides that can fit the swiper padding
    let slidesPerViewWithPadding = 0;

    if (this.edgeSlidesClickabe) {
      slidesPerViewWithPadding = Math.ceil(swiperPadding / slideWidth);
    }

    Array.from({ length: slidesPerView + slidesPerViewWithPadding * 2 }).forEach((_, i) => {
      swiper?.slides?.[swiper.activeIndex - slidesPerViewWithPadding + i]?.removeAttribute?.('inert');
    });
  }

  init() {
    if (this.swiperParams) {
      const swiperElement = this.carousel.querySelector<HTMLElement>('.swiper');

      if (!swiperElement) return;

      const swiper = new Swiper(swiperElement, this.swiperParams);

      swiper.on('afterInit', () => this.handleSwiperPreventTabbing(swiper));

      swiper.on('slideChange', () => this.handleSlideChange(swiper));

      swiper.init();

      this.swiper = swiper;

      window.addEventListener(stopAutoPlayEvent, () => {
        this.toggleAutoplay(true);
      });

      window.addEventListener(startAutoPlayEvent, () => {
        this.toggleAutoplay(false);
      });

      this.addKeyboardEventIntoNavArrows();
    }
  }

  changePaginationTabIndex() {
    if (!this.hasPaginationClickable) {
      const pages = this.carousel.querySelectorAll<HTMLAnchorElement>('.kf-m-pagination__page');

      pages?.forEach((page) => (page.tabIndex = -1));
    }
  }

  renderPagination(current: number, total: number) {
    const paginationContainer = this.carousel?.querySelector<HTMLElement>(getClass('pagination'));

    const paginationData: PaginationModel = {
      pages: Array.from(Array(total).keys()).map((page) => ({
        isCurrentPage: current === page + 1,
        label: (page + 1).toString(),
        isLastPage: total === page + 1,
      })),
      isBall: ['contentTile', 'propertyListing', 'insight', 'propertyCategory', 'podcast', 'insightGroup', 'categoryImage'].includes(
        this.itemType || ''
      ),
    };

    const paginationComponent = getComponentMarkup<ComponentProps<typeof Pagination>>(Pagination, paginationData);

    if (paginationContainer) paginationContainer.innerHTML = paginationComponent;

    this.addKeyboardEventIntoPagination();
    this.changePaginationTabIndex();
    this.addClickEventIntoPagination();

    return paginationComponent;
  }

  renderMobilePagination(current: number, total: number) {
    const paginationMobileContainer = this.carousel?.querySelector<HTMLElement>(getClass('pagination-mobile'));

    const paginationComponent = getComponentMarkup<ComponentProps<typeof CarouselMobilePagination>>(CarouselMobilePagination, {
      current,
      total,
    });

    if (paginationMobileContainer) paginationMobileContainer.innerHTML = paginationComponent;

    return paginationComponent;
  }

  handleOnBreakpoint(swiper: Swiper, breakpointParams: SwiperOptions) {
    // @ts-expect-error custom property
    this.maxPages = breakpointParams.pagination?.maxPages || 0;
    if (this.maxPages > 1 && typeof swiper.params.slidesPerView === 'number') {
      this.maxIndex = Math.min(this.maxPages * swiper.params.slidesPerView - 1, this.totalSlides - 1);

      if (swiper.realIndex > 0) swiper.slideTo(0, 0, true);

      const slides = swiper.slides.length > 0 ? swiper.slides : swiper.wrapperEl.querySelectorAll<HTMLElement>('.swiper-slide');

      slides?.forEach((slide, index) => {
        if (index > this.maxIndex) {
          slide.style.display = 'none';
        } else {
          slide.style.removeProperty('display');
        }
      });
    }
  }
}

window.addEventListener('DOMContentLoaded', () => {
  const carouselContainers = document.querySelectorAll<HTMLElement>('.kf-o-carousel');

  for (const carousel of carouselContainers) {
    const swiperElement = carousel.querySelector<HTMLElement>('.swiper');

    if (!swiperElement || swiperElement.classList.contains('swiper-initialized')) return;

    new Carousel(carousel).init();
  }
});

export { stopAutoPlayEvent, startAutoPlayEvent, Carousel as CarouselEntry };
